import { useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import placeHolder from "../../../src/assets/images/PlaceHolder.webp";
import { Button } from "primereact/button";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

const UploadFile = ({
    label,
    note,
    preview,
    original,
    uploadHandler,
    uploadRef,
    handleDelete,
    isEditAllow,
    aspectRatio = 1
}) => {
    const cropperRef = useRef(null);
    const [selectedRatio, setSelectedRatio] = useState(aspectRatio);
    const [cropVisible, setCropVisible] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [originalFile, setOriginalFile] = useState(null);
    const [cropSize, setCropSize] = useState(null);
    const ratios = [
        { label: "Free", value: NaN },
        { label: "1:1", value: 1 },
        { label: "1:2", value: 1 / 2 },
        { label: "2:1", value: 2 / 1 },
        { label: "3.55/1", value: 3.55/1},
        { label: "3:4", value: 3 /4 },
        { label: "4:3", value: 4 / 3 },
        { label: "16:9", value: 16 / 9 }
    ];

    const handleSelectFile = ({ files }) => {
        const file = files?.[0];
        if (!file || !file.type.startsWith("image/")) return;

        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result);
            setOriginalFile(file);
            setCropVisible(true);
        };
        reader.readAsDataURL(file);
    };

    const resetCrop = () => {
        setCropVisible(false);
        setImageSrc(null);
        setOriginalFile(null);
    };

    const applyCrop = async () => {
        try {
            const cropper = cropperRef.current?.cropper;
            if (!cropper || !originalFile) return;

            cropper.getCroppedCanvas().toBlob(
                (blob) => {
                    if (!blob) return;

                    const croppedFile = new File([blob], originalFile.name, {
                        type: originalFile.type
                    });

                    uploadHandler({ files: [croppedFile] });
                    resetCrop();
                },
                originalFile.type,
                0.9
            );
        } catch (err) {
            console.error("Crop failed:", err);
        }
    };

    const updateCropSize = () => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper) return;

        const data = cropper.getData(true);
        setCropSize({
            width: Math.round(data.width),
            height: Math.round(data.height),
        });
    };

    const changeAspectRatio = (ratio) => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper) return;

        setSelectedRatio(ratio);
        cropper.setAspectRatio(ratio);
    };

    return (
        <div style={{ height: "inherit" }}>
            <div style={{ paddingInline: 10 }}>
                <div>{label}</div>
                {note && <span style={{ color: "red" }}>{note}</span>}
            </div>

            <div
                style={{
                    padding: 10,
                    borderRadius: 8,
                    width: "100%",
                    height: "100%",
                    position: "relative"
                }}
            >
                <img
                    src={preview || original || placeHolder}
                    alt="preview"
                    onError={(e) => (e.target.src = placeHolder)}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 8
                    }}
                />

                {isEditAllow && (
                    <div className="user-profile-btn profile-position">
                        <div className="p-d-flex p-ai-center p-jc-center">
                            {!preview && (
                                <FileUpload
                                    ref={uploadRef}
                                    name="file"
                                    customUpload
                                    auto
                                    mode="basic"
                                    uploadHandler={handleSelectFile}
                                    accept="image/*"
                                />
                            )}

                            {(preview || original) && (
                                <Button
                                    icon="pi pi-trash"
                                    className="p-button-rounded p-button-danger p-button-sm p-upload-delete p-ml-2"
                                    onClick={() => handleDelete(label)}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Dialog
                header="Crop Image"
                visible={cropVisible}
                modal
                style={{ width: 650 }}
                onHide={resetCrop}
                footer={
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%"
                        }}
                    >
                        <span style={{ fontSize: 12, color: "#555" }}>
                            {cropSize ? `${cropSize.width} × ${cropSize.height} px` : "Adjust crop"}
                        </span>

                        <div style={{ display: "flex", gap: 10 }}>
                            <Button label="Cancel" className="p-button-text" onClick={resetCrop} />
                            <Button label="Crop & Upload" onClick={applyCrop} />
                        </div>
                    </div>
                }
            >
                {imageSrc && (
                    <div style={{ width: "100%" }}>
                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                                marginBottom: 12,
                                flexWrap: "wrap",
                                justifyContent: "center"
                            }}
                        >
                            {ratios?.map((r) => (
                                <div
                                    key={r.label}
                                    onClick={() => changeAspectRatio(r.value)}
                                    style={{
                                        padding: "6px 14px",
                                        borderRadius: 20,
                                        cursor: "pointer",
                                        fontSize: 13,
                                        border:
                                            selectedRatio === r.value ||
                                                (isNaN(selectedRatio) && isNaN(r.value))
                                                ? "2px solid #6366F1"
                                                : "1px solid #ccc",
                                        background:
                                            selectedRatio === r.value ||
                                                (isNaN(selectedRatio) && isNaN(r.value))
                                                ? "#eef2ff"
                                                : "#fff",
                                        fontWeight: 500
                                    }}
                                >
                                    {r.label}
                                </div>
                            ))}
                        </div>
                        <Cropper
                            ref={cropperRef}
                            src={imageSrc}
                            style={{ height: 420, width: "100%" }}
                            aspectRatio={selectedRatio}
                            guides={true}
                            viewMode={1}
                            dragMode="move"
                            background={false}
                            responsive={true}
                            autoCropArea={1}
                            checkOrientation={false}
                            cropend={updateCropSize}
                            zoom={updateCropSize}
                            ready={updateCropSize}
                        />
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default UploadFile;
